// package imports
import { forwardRef, ReactNode, useEffect, useRef } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { useMeasure } from 'react-use';
import { Session } from 'next-auth';
import { Theme as MuiTheme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// local imports
import { mergeRefs } from '../../../../services/reactUtils';
import {
  MegaMenuCTACard,
  NavigationItem,
  SiteHeaderType,
} from '../SiteHeaderTypes';
import { useMegaMenuStore } from './UseMegaMenuStore';
import { themeColor } from 'services/styleUtils/colorUtils';
import MegaMenuCtaCard from './MegaMenuCtaCard';
import {
  doSignOut,
  filterCtaCards,
  filterNavigationLinks,
} from '../SiteHeaderUtils';
import { SIGN_OUT_TEXT } from '../SiteHeaderConstants';
import { LessonPlan } from 'types/AgilityTypes';
import { useAsyncMemo } from 'services/hooks/useAsyncMemo';

function NavigationMenuInner({
  links,
  cards,
  session,
  lessonPlanOfTheWeek,
}: {
  links: NavigationItem[];
  cards: MegaMenuCTACard[];
  session: Session | null;
  lessonPlanOfTheWeek?: LessonPlan;
}) {
  const theme = useTheme() as MuiTheme;
  const columns = new Map<string, NavigationItem[]>();
  const cardColumns = new Map<string, MegaMenuCTACard[]>();

  links.forEach((link) => {
    const columnIndex = link.fields.columnNumber ?? '1';
    if (!columns.has(columnIndex)) {
      columns.set(columnIndex, []);
    }
    columns.get(columnIndex)?.push(link);
  });

  cards.forEach((card) => {
    const columnIndex = card.fields.columnNumber ?? '1';
    if (!cardColumns.has(columnIndex)) {
      cardColumns.set(columnIndex, []);
    }
    cardColumns.get(columnIndex)?.push(card);
  });

  const renderLinkColumn = (links: NavigationItem[], columnIndex: string) => {
    return (
      <ul key={columnIndex} className="grid gap-4">
        {links.map((link, index) => {
          if (link.fields?.isSeparatorLink === 'true') {
            // render a separator
            return (
              <li
                key={`separator-${link.contentID}`}
                className="border-b border-gray-300 mb-[-5px] w-[95%]"
              />
            );
          }
          return (
            <li
              key={link.contentID}
              className={
                index < links.length - 1 &&
                links[index + 1].fields?.isSeparatorLink === 'true'
                  ? 'mb-[-10px]  '
                  : ''
              }
            >
              <a
                style={{
                  color: themeColor(
                    theme,
                    link.fields?.textColor?.fields?.color ?? 'primary.main',
                  ),
                }}
                className="underline-offset-4 hover:underline font-semibold"
                onClick={async (e) => {
                  if (
                    link.fields?.link?.text?.toLowerCase() === SIGN_OUT_TEXT &&
                    session
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    await doSignOut(session);
                  }
                }}
                href={
                  link.fields?.link?.text?.toLowerCase() === SIGN_OUT_TEXT
                    ? '#'
                    : link.fields.link.href
                }
                target={link.fields.link.target}
                rel="noopener noreferrer"
              >
                {link.fields.link.text}
              </a>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderCardColumn = (cards: MegaMenuCTACard[], columnIndex: string) => {
    return (
      <MegaMenuCtaCard
        cardData={cards[0]}
        key={columnIndex}
        lessonPlanOfTheWeek={lessonPlanOfTheWeek}
      />
    );
  };

  return (
    <div className="grid grid-cols-3 gap-4 items-start">
      {['1', '2', '3'].map((columnIndex) => {
        if (columns.has(String(columnIndex))) {
          const columnLinks = columns.get(columnIndex);
          if (columnLinks) {
            return renderLinkColumn(columnLinks, columnIndex);
          }
        } else if (cardColumns.has(String(columnIndex))) {
          const columnCards = cardColumns.get(columnIndex);
          if (columnCards) {
            return renderCardColumn(columnCards, columnIndex);
          }
        } else {
          return <div key={columnIndex} className="" />;
        }
      })}
    </div>
  );
}

type AnimtedNavigationMenuInnerProps = {
  children: ReactNode;
  in?: boolean;
  id: string | number;
};

const AnimtedNavigationMenuInner = forwardRef<
  Element,
  AnimtedNavigationMenuInnerProps
>(({ children, in: inProp, id }, forwardedRef) => {
  const localRef = useRef<HTMLDivElement | null>(null);
  const ref = mergeRefs(forwardedRef, localRef);

  useEffect(() => {
    if (inProp) {
      localRef.current?.focus();
    }
  }, [inProp]);

  return (
    <Transition
      nodeRef={localRef}
      in={inProp}
      unmountOnExit
      timeout={{
        enter: 450,
        exit: 300,
      }}
    >
      {(state) => (
        <div
          ref={ref}
          tabIndex={-1}
          id={`nav-menu-${id}`}
          aria-labelledby={`nav-menu-button-${id}`}
          className={`transition
            ${state === 'entering' || state === 'entered' ? 'delay-150 opacity-100' : ''}
            ${state === 'exiting' || state === 'exited' ? 'opacity-0' : ''}
            ${state === 'exiting' ? 'absolute inset-0' : ''}
            ${state === 'exiting' || state === 'exited' ? 'translate-x-20' : ''}`}
        >
          {children}
        </div>
      )}
    </Transition>
  );
});

AnimtedNavigationMenuInner.displayName = 'AnimtedNavigationMenuInner';

const NavigationMenu = ({
  headerFields,
  session,
  lessonPlanOfTheWeek,
}: {
  headerFields: SiteHeaderType;
  session: Session | null;
  lessonPlanOfTheWeek?: LessonPlan;
}) => {
  const [sizeRef, { height }] = useMeasure();
  const { openMenu } = useMegaMenuStore();

  const section = headerFields.fields.megaMenuSections.find(
    (s) => String(s.contentID) === openMenu,
  );

  const [links, fetchingLinks] = useAsyncMemo(
    async () =>
      await filterNavigationLinks(section?.fields.links ?? [], session),
    [section, openMenu, session],
    null,
    `NavigationMenuLinks-${section?.contentID}`, // Cache key
    section?.contentID ? true : false, // Only cache if section has contentID
  );

  const [cards, fetchingCards] = useAsyncMemo(
    async () => await filterCtaCards(section?.fields?.cTACards ?? [], session),
    [section, openMenu, session?.user],
    null,
    `NavigationMenuCards-${section?.contentID}`, // Cache key
    section?.contentID ? true : false, // Only cache if section has contentID
  );

  if (!links) {
    return null;
  }

  return (
    <TransitionGroup
      className="relative transition-[height]"
      style={{
        height,
      }}
    >
      <AnimtedNavigationMenuInner
        key={section?.contentID}
        ref={openMenu === String(section?.contentID) ? sizeRef : undefined}
        id={String(section?.contentID)}
      >
        {!fetchingLinks &&
          !fetchingCards &&
          (links?.length > 0 || (cards && cards.length > 0)) && (
            <NavigationMenuInner
              links={links}
              cards={cards ?? []}
              session={session}
              lessonPlanOfTheWeek={lessonPlanOfTheWeek}
            />
          )}
        {(fetchingLinks || fetchingCards) && (
          <div className="grid grid-cols-3 gap-4 items-start">
            <div className="col-span-3 p-4">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-6 bg-gray-200 rounded w-full mb-2" />
              </div>
            </div>
          </div>
        )}
      </AnimtedNavigationMenuInner>
    </TransitionGroup>
  );
};

export default NavigationMenu;
